/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Button, Card, Modal, Space, Table, TablePaginationConfig, Input, Select } from 'antd';
import { DeleteOutlined, FormOutlined, InfoCircleOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { StbGroup, DeviceType, SuperGroup, StbGroupExtended, Rule, DeviceTypeSettings } from '@services/types';

import api from '@services/api';
import EditStbGroupModal from './EditStbGroupModal';
import InfoStbGroupPage from './InfoStbGroupPage';
import {
  ApiUserColumnsResponse,
  Role,
  StbGroupNameResponse,
  SystemHintsForStbGroupsResponse,
  UserRequest,
} from '@openapi';
import { toast } from 'react-toastify';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { getLabelByFirmwareType, getUserRoleByRoles } from '@services/utils';
import { selectSearchFilter } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';
import styles from './stbGroup.module.scss';
import RuleModal from '@layouts/devices/STBGroup/RuleModal';

const { Column } = Table;
const { Search } = Input;
const { Option } = Select;

interface SorterState {
  field?: string;
  order?: 'ascend' | 'descend' | undefined;
}
const STBGroupList: React.FC = () => {
  const {
    stbGroups,
    setStbGroups,
    deviceTypes,
    superGroups,
    setSelectedSuper,
    selectedSuper,
    setSelectedDevice,
    selectedDevice,
    hints,
    columnsStbEtc,
    setColumnsStbEtc,
    columnsStb,
    setColumnsStb,
  } = React.useContext(STBGroupContext);
  const { settings, user, deviceFilter, setDeviceFilter } = React.useContext(AppContext);
  const [searchResult, setSearchResult] = React.useState('');
  const [isShowIpGroup, setIsShowIpGroup] = useState(true);
  const [isShowQuota, setIsShowQuota] = useState(true);
  const [isShowModeDeploy, setIsShowModeDeploy] = useState(true);
  const [isShowBpProfile, setIsShowBpProfile] = useState(true);
  const [ruleModal, setRuleModal] = React.useState<StbGroupExtended>();
  const [settingsDevice, setSettingsDevice] = React.useState<DeviceTypeSettings>();
  const [additionalData, setAdditionalData] = useState('');
  const [stbFilterValue, setStbFilterValue] = useState<string | null>();
  const [stbGroupsFilter, setStbGroupsFilter] = useState<Array<StbGroupNameResponse>>([]);
  const [openModal, setOpenModal] = useState<StbGroup | null>();
  const [sortOrder, setSortOrder] = useState<SorterState>({});
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: stbGroups.length,
  });

  const history = useHistory();

  React.useEffect(() => {
    if (selectedDevice) {
      api.administration.getDeviceTypeSettings(selectedDevice).then((data) => setSettingsDevice(data));
    }
  }, [selectedDevice]);

  const handleOk = async (stbGroup: StbGroup | any, inputData: string) => {
    try {
      if (inputData === stbGroup?.name) {
        await api.stbGroups.deleteStbGroup(stbGroup.id);
        setAdditionalData('');
        setStbGroups(stbGroups.filter(({ id }) => id !== stbGroup.id));
        toast.success('Stb Group was deleted successfully');
        setOpenModal(null);
      } else {
        toast.error('Stb Group name is incorrect');
      }
    } catch (error) {
      console.log('Stb Group was not deleted ~ error', error);
      toast.error('Stb Group was not deleted');
    }
  };

  const onSearch = (newPagination = pagination, sorter = sortOrder) => {
    const params: any = {
      limit: newPagination.pageSize,
      offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
    };
    if (sorter?.order) {
      params.direction = sorter?.order === 'ascend' ? 'ASC' : 'DESC';
      params.orderBy = sorter?.field;
    }
    if (deviceFilter?.length) {
      return api.stbGroups
        .getStbGroups({
          ...params,
          filterDeviceTypeId: deviceFilter,
        })
        .then((data) => {
          setStbGroups(data.elements);
          setPagination((prevState) => ({
            ...prevState,
            total: data.totalElementCount || data.elements.length,
          }));
          setDeviceFilter('');
        });
    }
    return api.stbGroups
      .getStbGroups({
        ...params,
        filter: searchResult,
        filterDeviceTypeId: selectedDevice,
        filterSuperGroupId: selectedSuper,
        filterStbGroupId: stbFilterValue,
      })
      .then((data) => {
        setStbGroups(data.elements);
        setPagination((prevState) => ({
          ...prevState,
          total: data.totalElementCount || data.elements.length,
        }));
      });
  };

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig, sorter: any) => {
      setPagination(newPagination);
      setSortOrder(sorter);
      onSearch(newPagination, sorter);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResult],
  );

  React.useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, selectedSuper, stbFilterValue]);

  React.useEffect(() => {
    api.stbGroups.getStbGroupsFilter().then((data) => {
      setStbGroupsFilter(data);
    });
  }, []);

  React.useEffect(() => {
    setIsShowIpGroup(stbGroups.some((item) => item.ipMask));
    setIsShowQuota(stbGroups.some((item) => item.quota && settingsDevice?.enableQuota));
    setIsShowModeDeploy(stbGroups.some((item) => item.deploymentMode && settingsDevice?.enableDeploymentMode));
    setIsShowBpProfile(stbGroups.some((item) => item.bpProfile && settingsDevice?.addBPProfile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceTypes, stbGroups, superGroups]);

  const stbCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const stbItem = (stbCount || 0) - ((pagination.pageSize || 0) - 1);

  const handleChange = (value: string[]) => {
    setColumnsStb(value);
    const newItems = [...(columnsStb || []), ...(columnsStbEtc || [])].filter((item) => !value.includes(item));
    setColumnsStbEtc(newItems);
  };

  React.useEffect(() => {
    if (user?.id && columnsStb) {
      api.administration.updateColumnsStbGroup(user.id, { columns: columnsStb });
    }
  }, [columnsStb]);

  const role: UserRequest['role'] | any = getUserRoleByRoles(user?.roles || []);

  return (
    <Card
      title={`Groups: ${stbItem}-${stbCount} / ${pagination.total}`}
      extra={
        <Space className={styles.item}>
          <div className={styles.filter}>
            <Select
              className={styles.selectDevice}
              placeholder="Select a Device Type"
              onChange={(e: DeviceType['id']) => {
                setSelectedDevice(e);
                setSelectedSuper(undefined);
                setPagination((prevState) => ({
                  ...prevState,
                  current: 1,
                }));
              }}
              value={selectedDevice}
              showSearch
              allowClear
              filterOption={selectSearchFilter}
            >
              {deviceTypes?.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.deviceName}
                </Option>
              ))}
            </Select>
            <Select
              className={styles.selectDevice}
              placeholder="Select Scope"
              onChange={(e: SuperGroup['id']) => {
                setSelectedSuper(e);
                setPagination((prevState) => ({
                  ...prevState,
                  current: 1,
                }));
              }}
              allowClear
              showSearch
              value={selectedSuper}
              filterOption={selectSearchFilter}
            >
              {superGroups?.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
            <Select
              onChange={(e: StbGroup['id']) => {
                setStbFilterValue(e);
              }}
              placeholder="Select StbGroup"
              allowClear
              showSearch
              filterOption={selectSearchFilter}
            >
              {stbGroupsFilter?.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
            <Search
              onChange={(e) => {
                setSearchResult(e.target.value);
              }}
              placeholder="Search"
              allowClear
              enterButton
              size="middle"
              onSearch={() => {
                onSearch();
              }}
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '300px max-content',
              gridGap: '10px',
              alignItems: 'center',
            }}
          >
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', minWidth: '300px', maxWidth: '700px' }}
              placeholder="Please select columns"
              filterOption={selectSearchFilter}
              value={columnsStb}
              onChange={handleChange}
              maxTagCount={2}
            >
              {columnsStb?.map((item) => (
                <Select.Option key={item}>{item}</Select.Option>
              ))}
              {columnsStbEtc?.map((item) => (
                <Select.Option key={item}>{item}</Select.Option>
              ))}
            </Select>
            {role && Role.Quality !== role && (
              <Button type="primary" onClick={() => history.push(Navigation.CreateSTBGroup)}>
                Add New Group
              </Button>
            )}
          </div>
        </Space>
      }
      bodyStyle={{ padding: 0 }}
    >
      <Table
        dataSource={stbGroups}
        pagination={pagination}
        onChange={(paginationArg, filterArg, sorterArg) => {
          handleTableChange(paginationArg, sorterArg);
        }}
        rowKey={(record: StbGroupExtended) => record.id}
        scroll={{ x: 1300 }}
      >
        <Column
          title=""
          width="50px"
          align="center"
          dataIndex="isDefault"
          render={(_text, record: StbGroupExtended) =>
            record.id.startsWith('Default') ? (
              <StarFilled style={{ color: '#FF9602' }} />
            ) : (
              <StarOutlined style={{ color: '#FF9602' }} />
            )
          }
        />
        {columnsStb?.includes('Size') && (
          <Column
            title="Size"
            align="center"
            width="100px"
            dataIndex="size"
            render={(_text, record: StbGroupExtended) => record.etc.deviceCount}
            sorter={true}
            sortDirections={['ascend', 'descend']}
          />
        )}
        {columnsStb?.includes('Device Type') && (
          <Column
            align="center"
            title="Device Type"
            dataIndex="deviceType"
            sorter={true}
            render={(_text, record: StbGroupExtended) => record.deviceTypeName}
            sortDirections={['ascend', 'descend']}
          />
        )}
        {columnsStb?.includes('Name') && (
          <Column align="center" title="Name" dataIndex="name" sorter={true} sortDirections={['ascend', 'descend']} />
        )}
        {columnsStb?.includes('Description') && <Column align="center" title="Description" dataIndex="description" />}
        {columnsStb?.includes('Update rule') && (
          <Column
            title="Update rule"
            align="center"
            dataIndex="upgradeRule"
            render={(_text, record: StbGroupExtended) => (
              <span
                style={{ color: '#00a9f7', cursor: 'pointer', fontWeight: '500' }}
                onClick={() => {
                  setRuleModal(record);
                }}
              >
                {record.etc.upgradeRuleName}
              </span>
            )}
            sorter={true}
            sortDirections={['ascend', 'descend']}
          />
        )}
        {columnsStb?.includes('Scope') && (
          <Column
            title="Scope"
            align="center"
            dataIndex="scope"
            render={(_text, record: StbGroupExtended) => record.etc.superGroupName}
            sorter={true}
            sortDirections={['ascend', 'descend']}
          />
        )}
        {hints?.enableIpGroup && settings?.enableIpGroup && isShowIpGroup && columnsStb?.includes('IP Mask') && (
          <Column title="IP Mask" dataIndex="ipMask" render={(_text, record: StbGroupExtended) => record.ipMask} />
        )}
        {isShowQuota && columnsStb?.includes('Quota') ? (
          <Column
            title="Quota"
            align="center"
            dataIndex="quota"
            render={(_text, record: StbGroupExtended) =>
              record.quota ? (
                <span>
                  {record.etc.deployedStbGroupCount || 0} / {_text}
                  {getLabelByFirmwareType(record.quotaFirmwareType, settings)} (+
                  {record.quotaDailyIncrement}
                  /day)
                </span>
              ) : null
            }
          />
        ) : null}
        {isShowModeDeploy && columnsStb?.includes('Deployment Mode') && (
          <Column align="center" title="Deployment Mode" dataIndex="deploymentMode" />
        )}
        {isShowBpProfile && columnsStb?.includes('BP Profile') && (
          <Column align="center" title="BP Profile" dataIndex="bpProfile" />
        )}
        <Column
          title="Action"
          key="action"
          align="center"
          width="120px"
          fixed="right"
          render={(_text, newStbGroup: StbGroup) => (
            <Space size="middle">
              <InfoCircleOutlined
                onClick={() => history.push(Navigation.STBGroupInfoById.replace(':id', newStbGroup.id))}
              />
              <>
                {role !== Role.Quality && (
                  <>
                    <FormOutlined
                      onClick={() => history.push(Navigation.UpdateSTBGroup.replace(':id', newStbGroup.id))}
                    />
                  </>
                )}
                {(role === Role.Superadmin || (role === Role.Integration && !newStbGroup.id.startsWith('Default'))) && (
                  <DeleteOutlined onClick={() => setOpenModal(newStbGroup)} />
                )}
              </>
            </Space>
          )}
        />
      </Table>
      <Modal
        title={'Are you sure delete this Stb Group?'}
        destroyOnClose={true}
        okText={'Delete'}
        okType={'danger'}
        cancelText={'Cancel'}
        onCancel={() => {
          setAdditionalData('');
          setOpenModal(undefined);
        }}
        visible={!!openModal}
        onOk={() => handleOk(openModal, additionalData)}
      >
        <div>
          <p>Please write the Group name for deletion:</p>
          <Input
            onChange={(e) => {
              setAdditionalData(e.target.value);
            }}
          />
        </div>
      </Modal>
      <RuleModal
        ruleModal={ruleModal}
        visible={!!ruleModal}
        onClose={() => {
          setRuleModal(undefined);
        }}
      />
    </Card>
  );
};

interface Context {
  rules: Rule[];
  stbGroups: StbGroupExtended[];
  deviceTypes: DeviceType[];
  superGroups: SuperGroup[];
  hints: SystemHintsForStbGroupsResponse | null;
  setRules: (value: Rule[]) => void;
  setStbGroups: (value: StbGroupExtended[]) => void;
  setDeviceTypes: (value: DeviceType[]) => void;
  setSuperGroups: (value: SuperGroup[]) => void;
  setHints: (value: SystemHintsForStbGroupsResponse) => void;
  setSelectedSuper: (values: StbGroupExtended['superGroupId'] | undefined) => void;
  selectedSuper: StbGroupExtended['superGroupId'] | undefined;
  setSelectedDevice: (values: StbGroupExtended['deviceTypeId'] | undefined) => void;
  selectedDevice: StbGroupExtended['deviceTypeId'] | undefined;
  columnsStb: ApiUserColumnsResponse['columns'] | undefined;
  setColumnsStb: (values: ApiUserColumnsResponse['columns'] | undefined) => void;
  columnsStbEtc: ApiUserColumnsResponse['etc'] | undefined;
  setColumnsStbEtc: (values: ApiUserColumnsResponse['etc'] | undefined) => void;
}

export const STBGroupContext = React.createContext<Context>({
  rules: [],
  stbGroups: [],
  deviceTypes: [],
  superGroups: [],
  selectedDevice: '',
  selectedSuper: '',
  hints: null,
  setRules: null as any,
  setStbGroups: null as any,
  setDeviceTypes: null as any,
  setSuperGroups: null as any,
  setHints: null as any,
  setSelectedSuper: null as any,
  setSelectedDevice: null as any,
  columnsStb: null as any,
  setColumnsStb: null as any,
  columnsStbEtc: null as any,
  setColumnsStbEtc: null as any,
});

const STBGroup = () => {
  const { user, columnsStb, setColumnsStb, columnsStbEtc, setColumnsStbEtc } = React.useContext(AppContext);
  const [rules, setRules] = useState<Rule[]>([]);
  const [stbGroups, setStbGroups] = useState<StbGroupExtended[]>([]);
  const [stbRawGroups, setRawStbGroups] = useState<StbGroupExtended[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [superGroups, setSuperGroups] = useState<SuperGroup[]>([]);
  const [hints, setHints] = useState<SystemHintsForStbGroupsResponse | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<StbGroupExtended['deviceTypeId']>();
  const [selectedSuper, setSelectedSuper] = useState<StbGroupExtended['superGroupId']>();

  React.useEffect(() => {
    if (user?.id) {
      Promise.all([
        api.administration.getDeviceTypes(),
        api.administration.getSuperGroups(),
        api.rules.getRules(),
        api.systemHints.getSystemHints(),
      ]).then(([newDeviceTypes, newSuperGroups, newRules, systemHints]) => {
        setDeviceTypes(newDeviceTypes);
        setSuperGroups(newSuperGroups);
        setRules(newRules.elements);
        setHints(systemHints);
      });
    }
  }, []);

  React.useEffect(() => {
    setStbGroups(
      stbRawGroups.map((group) => ({
        ...group,
        deviceTypeName: deviceTypes.find((deviceType) => deviceType.id === group.deviceTypeId)?.deviceName,
        superGroupName: superGroups.find((superGroup) => superGroup.id === group.deviceTypeId)?.name,
      })),
    );
  }, [stbRawGroups, deviceTypes, superGroups]);

  return (
    <STBGroupContext.Provider
      value={{
        rules,
        stbGroups,
        deviceTypes,
        superGroups,
        hints,
        selectedDevice,
        selectedSuper,
        setRules,
        setStbGroups: setRawStbGroups,
        setDeviceTypes,
        setSuperGroups,
        setHints,
        setSelectedSuper,
        setSelectedDevice,
        columnsStb,
        setColumnsStb,
        columnsStbEtc,
        setColumnsStbEtc,
      }}
    >
      <Switch>
        <Route exact path={Navigation.STBGroup} component={STBGroupList} />
        <Route exact path={Navigation.CreateSTBGroup} component={EditStbGroupModal} />
        <Route exact path={Navigation.UpdateSTBGroup} component={EditStbGroupModal} />
        <Route exact path={Navigation.STBGroupInfoById} component={InfoStbGroupPage} />
      </Switch>
    </STBGroupContext.Provider>
  );
};

export default React.memo(STBGroup);
